import { useState } from 'react'
import domPurify from 'dompurify'
import moment, { Moment } from 'moment'
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined'
import {
  Button,
  TextField,
  Select,
  FormControl,
  MenuItem,
  Tooltip,
  Checkbox,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Modal,
  Divider,
} from '@resiliatech/birdsong'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { DatePicker } from 'Components/SharedComponents/DatePicker/DatePicker'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  AddGoalInput,
  GoalCategory,
  GoalType,
  Goal,
  GoalUpdateInput,
  GoalImpactCategory,
} from 'Generated/graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { categories, impactCategories } from 'TypeDefs/Goal'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import CREATE_CHART from 'Apollo/Mutations/CreateChart'
import ADD_GOAL from 'Apollo/Mutations/AddGoal'
import { useMutation, useApolloClient } from '@apollo/client'
import { CHART } from 'Apollo/Queries/Chart'
import { CHART_CATEGORY } from 'Apollo/Queries/ChartCategory'
import { GOALS } from 'Apollo/Queries/Goals'
import {
  Buttons,
  DatePickers,
  GoalModalContainer,
  Header,
  Inputs,
  ShareFunderText,
  FunderCheckBox,
  UnshareWarning,
  TooltipText,
  TooltipBox,
  CategorySelector,
} from './GoalModal.styled'
import { GOAL_DATE_FORMAT } from '../types'

type GoalModalProps = {
  isOpen: boolean
  onClose: () => void
  onCreateGoal?: (goal: AddGoalInput) => Promise<unknown>
  goal?: Goal
  onUpdateGoal?: (goal: GoalUpdateInput) => Promise<void>
}

export const GoalModal = ({
  isOpen,
  onClose,
  onCreateGoal,
  goal,
  onUpdateGoal,
}: GoalModalProps) => {
  const {
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      platformPlan,
      role,
      nonprofitOrgRole,
      activeFunders,
    },
  } = useAppState()
  const { teamOptions } = useNonprofitOrgTeam()
  const apolloClient = useApolloClient()
  const [title, setTitle] = useState(goal?.title || '')
  const [description, setDescription] = useState(goal?.description || '')
  const [impactCategory, setImpactCategory] =
    useState<GoalImpactCategory | null>(
      goal?.impactCategory || GoalImpactCategory.Mission
    )
  const [category, setCategory] = useState<GoalCategory>(
    goal?.category || GoalCategory.None
  )
  const [ownerId, setOwnerId] = useState<number | null>(goal?.owner?.id || null)
  const [startDate, setStartDate] = useState<Moment | null>(
    goal?.startDate ? moment.utc(goal?.startDate) : null
  )
  const [endDate, setEndDate] = useState<Moment | null>(
    goal?.endDate ? moment.utc(goal?.endDate) : null
  )
  const [isCategorySelectOpen, setIsCategorySelectOpen] = useState(false)
  const [isGoalTypeSelectOpen, setIsGoalTypeSelectOpen] = useState(false)
  const [isOwnerSelectOpen, setIsOwnerSelectOpen] = useState(false)
  const funderSharedGoal = goal?.FunderOrgToSharedGoal.find(
    (sharedGoal) => sharedGoal.goalId === goal?.id && !sharedGoal.archivedAt
  )

  const [isSharedWithFunder, setIsSharedWithFunder] = useState(
    !!funderSharedGoal
  )

  // Chart related states
  const [unitOfMeasurement, setUnitOfMeasurement] = useState('')
  const [targetValue, setTargetValue] = useState<number | null>(null)

  // Add mutation for creating goals directly, so we can get the result
  const [addGoalMutation] = useMutation(ADD_GOAL, {
    refetchQueries: [
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
    ],
  })

  const [createChartMutation] = useMutation(CREATE_CHART, {
    refetchQueries: [
      {
        query: CHART,
        variables: { goalId: goal?.id },
      },
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
    ],
    awaitRefetchQueries: true,
  })

  const hasActiveFunders = activeFunders && activeFunders.length > 0

  // Reset payload for task-to-upsert on modal close
  const handleClose = () => {
    setTitle('')
    setDescription('')
    setCategory(GoalCategory.None)
    setOwnerId(null)
    setStartDate(null)
    setEndDate(null)
    setUnitOfMeasurement('')
    setTargetValue(null)
    onClose()
  }

  const dispatchCreateGoalSubmitted = () => {
    if (goal) {
      analytics.trackActionCreator(initiatives.Create_Goal_Submitted)({
        goalCreatedDate: goal.createdAt,
        goalUpdatedDate: goal.updatedAt,
        goalTitle: goal.title,
      })
    }
  }

  const dispatchShareGoalWithFunderChecked = () => {
    analytics.trackActionCreator(goals.Goal_Share_Funder_Toggle_Clicked)({
      goalId: goal?.id,
      goalTitle: goal?.title,
      goalUpdatedDate: goal?.updatedAt,
      isGoalSharedWithFunder: !isSharedWithFunder,
      platformPlan,
      relationship: nonprofitOrgRole,
      userAccountType: role,
    })
  }

  const handleCreateGoal = async () => {
    performance.mark('goal form submit')

    try {
      // Prepare goal input data
      const goalInfo = {
        category,
        description: description || null,
        endDate: endDate ? endDate.toISOString() : null,
        impactCategory,
        nonprofitOrgId,
        ownerId,
        startDate: startDate ? startDate.toISOString() : null,
        title,
        type: GoalType.UserCustom,
        shareWithFunderOrgIds:
          isSharedWithFunder && hasActiveFunders ? activeFunders : [],
        isGoalSharedByNonprofit:
          isSharedWithFunder && hasActiveFunders ? true : undefined,
      }

      // Always create a chart for new goals when unit of measurement is provided
      const shouldCreateChart = unitOfMeasurement && !goal

      // First call the parent's onCreateGoal in all cases
      if (onCreateGoal) {
        await onCreateGoal(goalInfo)
      }

      // If we need to create a chart, we need to create the goal ourselves to get the ID
      if (shouldCreateChart) {
        // Add a delay to avoid conflicts with the parent's goal creation
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 100)
        })

        // Find the newly created goal - use title and creation date to identify
        const now = new Date()
        const cutoffTime = new Date(now.getTime() - 10000) // 10 seconds ago

        const { data: goalsData } = await new Promise<{
          data: { goals: Goal[] }
        }>((resolve) => {
          setTimeout(() => {
            // Use IIFE pattern without void
            ;(async () => {
              const result = await apolloClient.query({
                query: GOALS,
                variables: {
                  activeOnly: true,
                  nonprofitOrgId,
                },
                fetchPolicy: 'network-only',
              })
              resolve(result)
            })()
          }, 1000) // Wait for goals to be available
        })

        // Find the most recently created goal with matching title
        const recentGoal = goalsData?.goals?.find(
          (g: Goal) => g.title === title && new Date(g.createdAt) > cutoffTime
        )

        // If we found the goal, use it; otherwise create it ourselves
        let goalId = recentGoal?.id

        if (!goalId) {
          // If we couldn't find the goal, create it ourselves
          const { data } = await addGoalMutation({
            variables: { goalInfo },
          })
          goalId = data?.addGoal?.id
        }

        // Create chart with the goal ID we got
        if (goalId) {
          // Create chart without empty catch block
          const result = await createChartMutation({
            variables: {
              data: {
                calculationType: 'INCREMENTAL',
                chartCategories: [{ title: unitOfMeasurement }],
                goalId,
                target: targetValue,
                nonprofitOrgId,
                title, // Use the same title as the goal
                unitOfMeasurement,
              },
            },
            refetchQueries: [
              {
                query: GOALS,
                variables: {
                  activeOnly: true,
                  includeOnboarding: true,
                  nonprofitOrgId,
                },
              },
            ],
          }).catch(
            () =>
              // Errors are handled silently
              null
          )

          // Manually refetch CHART and CHART_CATEGORY queries to ensure EditChartDataTable works properly
          if (result?.data?.createChart?.id) {
            const chartId = result.data.createChart.id

            // Refetch the chart data with the new goal ID
            await apolloClient.query({
              query: CHART,
              variables: { goalId },
              fetchPolicy: 'network-only',
            })

            // Refetch chart category data
            await apolloClient.query({
              query: CHART_CATEGORY,
              variables: { chartId },
              fetchPolicy: 'network-only',
            })
          }
        }
      }
    } catch {
      // Global error handler - errors are handled silently
      // Could add a toast notification system here in the future
    }

    dispatchCreateGoalSubmitted()
  }

  const handleUpdateGoal = async () => {
    if (onUpdateGoal && goal) {
      await onUpdateGoal({
        id: goal.id,
        category,
        description: description || null,
        endDate: endDate ? endDate.toISOString() : null,
        impactCategory,
        ownerId,
        startDate: startDate ? startDate.toISOString() : null,
        title,
        shareWithFunderOrgIds:
          isSharedWithFunder && hasActiveFunders ? activeFunders : [],
        isGoalSharedByNonprofit:
          isSharedWithFunder && hasActiveFunders ? true : undefined,
      })
    }
  }

  const onCheckboxChangeHandler = async () => {
    setIsSharedWithFunder(!isSharedWithFunder)
    dispatchShareGoalWithFunderChecked()
  }

  const categoryOptions = Object.entries(categories)
    .filter(([cat]) => cat !== GoalCategory.None)
    .map(([cat, displayValue]) => (
      <MenuItem key={cat} value={cat}>
        {displayValue}
      </MenuItem>
    ))

  const ownerOptions = teamOptions.map((option) => (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  ))

  const isValid =
    title && (category !== GoalCategory.None || impactCategory === 'MISSION')

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      disableEnforceFocus
    >
      <GoalModalContainer>
        <Header>{goal ? 'Edit Goal' : 'New Goal'}</Header>
        <Inputs>
          <TextField
            id='goalModalInputName'
            label='Title'
            placeholder='Start by giving your goal a focused title that captures the main objective.'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(title)}
            onChange={({ target: { value } }) => setTitle(value)}
            required
          />

          {!goal && (
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <TextField
                  id='chartTargetValue'
                  label='Numerical Target'
                  placeholder='Enter a target'
                  variant='outlined'
                  size='small'
                  type='number'
                  fullWidth
                  value={targetValue || ''}
                  onChange={({ target: { value } }) =>
                    setTargetValue(Number(value) || null)
                  }
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  id='chartUnitOfMeasurement'
                  label='Unit'
                  placeholder='Ex: houses rebuilt, students served.'
                  variant='outlined'
                  size='small'
                  fullWidth
                  value={unitOfMeasurement}
                  onChange={({ target: { value } }) =>
                    setUnitOfMeasurement(value)
                  }
                  required
                />
              </div>
            </div>
          )}

          <FormControl>
            <FormLabel>Goal Type</FormLabel>
            <Select
              id='goalModalInputSelectType'
              displayEmpty
              value={impactCategory || ''}
              onChange={({ target: { value } }) => {
                if (
                  ((val: string): val is GoalImpactCategory =>
                    (Object.values(GoalImpactCategory) as string[]).includes(
                      val
                    ))(value)
                ) {
                  setImpactCategory(value)
                }
              }}
              onClose={() => setIsGoalTypeSelectOpen(false)}
              onOpen={() => setIsGoalTypeSelectOpen(true)}
              open={isGoalTypeSelectOpen}
              sx={{ height: '40px' }}
              required
            >
              <MenuItem value='' disabled>
                <span style={{ color: '#757575' }}>Select a goal type*</span>
              </MenuItem>
              <MenuItem value={GoalImpactCategory.Mission}>
                {impactCategories[GoalImpactCategory.Mission]}
              </MenuItem>
              <MenuItem value={GoalImpactCategory.Organizational}>
                {impactCategories[GoalImpactCategory.Organizational]}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Goal Category</FormLabel>
            <Select
              id='goalModalInputSelectCategory'
              displayEmpty
              value={category}
              onChange={({ target: { value } }) =>
                setCategory(value as GoalCategory)
              }
              onClose={() => setIsCategorySelectOpen(false)}
              onOpen={() => setIsCategorySelectOpen(true)}
              open={isCategorySelectOpen}
              sx={{ height: '40px' }}
              required={impactCategory !== 'MISSION'}
              disabled={impactCategory === 'MISSION'}
              placeholder='Select a category*'
            >
              <MenuItem value={GoalCategory.None}>
                <CategorySelector>Select a category*</CategorySelector>
              </MenuItem>
              {categoryOptions}
            </Select>
          </FormControl>
          <TextField
            id='goalModalInputDescription'
            label='Add a brief description to your goal'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(description)}
            onChange={({ target: { value } }) => setDescription(value)}
          />
          <FormControl>
            <FormLabel>Owner</FormLabel>
            <Select
              id='goalModalInputSelectOwner'
              displayEmpty
              value={ownerId ?? 0}
              onChange={({ target: { value } }) => {
                setOwnerId(parseInt(String(value), 10))
              }}
              onClose={() => setIsOwnerSelectOpen(false)}
              onOpen={() => setIsOwnerSelectOpen(true)}
              open={isOwnerSelectOpen}
              sx={{ height: '40px' }}
              placeholder='Assign an owner for this goal'
            >
              <MenuItem value={0}>Assign an owner for this goal</MenuItem>
              {ownerOptions}
            </Select>
          </FormControl>
          <DatePickers>
            <div style={{ width: '50%' }}>
              <FormLabel>Start Date (Optional)</FormLabel>
              <DatePicker
                date={startDate ? startDate.format(GOAL_DATE_FORMAT) : null}
                onDateSelected={setStartDate}
                styles={{ width: '100%' }}
              />
            </div>
            <div style={{ width: '50%' }}>
              <FormLabel>End Date (Optional)</FormLabel>
              <DatePicker
                date={endDate ? endDate.format(GOAL_DATE_FORMAT) : null}
                onDateSelected={setEndDate}
                styles={{ width: '100%' }}
              />
            </div>
          </DatePickers>
          {hasActiveFunders && (
            <>
              <FunderCheckBox>
                <FormControlLabel
                  label={
                    <ShareFunderText>
                      Share goal with all funder(s)
                    </ShareFunderText>
                  }
                  control={
                    <Checkbox
                      defaultChecked={isSharedWithFunder}
                      color='primary'
                      onChange={onCheckboxChangeHandler}
                    />
                  }
                />
                <Tooltip
                  title={
                    <TooltipBox>
                      <TooltipText>
                        By checking this box, you are allowing us to share
                        progress of this goal with all your funders on their own
                        view of Goals via the funder platform. If you change
                        your mind, you can easily uncheck this option while
                        editing your goal and we will stop sharing this
                        information with them.
                      </TooltipText>
                    </TooltipBox>
                  }
                  arrow
                  placement='right'
                >
                  <HelpOutlinedIcon color='primary' />
                </Tooltip>
              </FunderCheckBox>
              {goal && funderSharedGoal && !isSharedWithFunder && (
                <UnshareWarning>
                  <WarningAmberIcon sx={{ marginRight: '10px' }} />
                  After saving your changes, progress on this goal will no
                  longer be shared with your funder(s).
                </UnshareWarning>
              )}
            </>
          )}
        </Inputs>
        <Buttons>
          <Button
            label='Cancel'
            onClick={handleClose}
            type='button'
            variant='tertiary'
          />
          <Button
            label={goal ? 'Save' : 'Create'}
            onClick={goal ? handleUpdateGoal : handleCreateGoal}
            type='button'
            variant='primary'
            isDisabled={!isValid || (!goal && !unitOfMeasurement)}
          />
        </Buttons>
      </GoalModalContainer>
    </Modal>
  )
}
